// src/lib/crashDiagnostics.ts
//
// 白画面インシデント（React error #310 等）の原因切り分け用、診断ツール。
// 2026-07-19：デプロイ直後に本番で白画面が複数回発生。CDN伝播中の版ずれ（古いJSと
// 新しいAPI/チャンクの混線）が原因ではないかという仮説があるが、確証がないため、
// 発生時に自動で証拠を残す仕組みとして追加。
//
// 仕組み：エラー発生時、①今表示中のページのJSバンドルが「ビルド時に焼き込まれた」
// gitコミット（NEXT_PUBLIC_GIT_COMMIT_SHA）と、②同じ瞬間に /api/debug/deploy-info を
// 呼んで得られる「今まさにサーバーが実行中の」gitコミットを比較する。食い違っていれば
// CDN伝播中の版ずれが濃厚、一致していれば今のコード自体の実バグ、と判定できる。
//
// 実装の変遷：
// ①初回（__NEXT_DATA__.buildIdを使う版）はApp RouterではbuildIdが取得できず機能せず。
// ②VERCEL_GIT_COMMIT_SHAを使う方式に変更（本ファイル）。
// ③window.onerror/unhandledrejectionだけでは、Next.js標準の内部エラーバウンダリが
//   エラーを先に捕まえてしまうケースを検知できないことが判明（2026-07-19）。
//   src/app/global-error.tsx を追加し、diagnoseCommitMismatch() をそちらからも
//   呼び出すことで、Next.js側に捕まった場合も画面上に直接結果を表示できるようにした。
//
// 既存ロジックには一切干渉しない（読み取り専用・console出力／画面表示のみ）。

declare global {
  interface Window {
    __crashDiagnosticsInit?: boolean;
  }
}

export type CommitDiagnosis = {
  myCommit: string | null;
  liveCommit: string | null;
  status: "mismatch" | "match" | "inconclusive";
  判定: string;
};

function getMyCommit(): string | null {
  const v = process.env.NEXT_PUBLIC_GIT_COMMIT_SHA;
  return v && v !== "unknown" ? v : null;
}

async function getLiveCommit(): Promise<string | null> {
  try {
    const res = await fetch("/api/debug/deploy-info", { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.commit === "string" ? data.commit : null;
  } catch {
    return null;
  }
}

// 白画面インシデントの切り分け本体。window.onerror経由でもglobal-error.tsx経由でも
// 同じ判定ロジックを使う。
export async function diagnoseCommitMismatch(): Promise<CommitDiagnosis> {
  const myCommit = getMyCommit();
  const liveCommit = await getLiveCommit();

  if (!myCommit || !liveCommit) {
    return {
      myCommit,
      liveCommit,
      status: "inconclusive",
      判定: "取得失敗のため判定不能（診断ツール自体の不具合の可能性。要確認）",
    };
  }
  if (myCommit !== liveCommit) {
    return {
      myCommit,
      liveCommit,
      status: "mismatch",
      判定: "版ずれあり → デプロイ直後のCDN伝播が原因の可能性が高い",
    };
  }
  return {
    myCommit,
    liveCommit,
    status: "match",
    判定: "版ずれなし・今動いているコードと一致 → コード自体のバグの可能性が高い（要調査）",
  };
}

let hasLoggedWindowError = false;

async function reportWindowError(context: string, detail: unknown) {
  // 1ページの滞在中に何度もログを吐かないための簡易ガード
  if (hasLoggedWindowError) return;
  hasLoggedWindowError = true;

  const diagnosis = await diagnoseCommitMismatch();

  console.error("[crash-diagnostics] コミット比較（window.onerror経由）", {
    context,
    detail,
    ...diagnosis,
    time: new Date().toISOString(),
  });
}

// 注意：Next.jsの内部エラーバウンダリがReactの描画エラーを先に捕まえてしまう場合、
// window.onerror/unhandledrejectionは一切発火しない（2026-07-19に実際に確認済み）。
// その場合の受け皿は src/app/global-error.tsx 側が担う。
export function initCrashDiagnostics() {
  if (typeof window === "undefined") return;
  if (window.__crashDiagnosticsInit) return;
  window.__crashDiagnosticsInit = true;

  window.addEventListener("error", (event: ErrorEvent) => {
    reportWindowError("window.onerror", {
      message: event.error?.message ?? event.message,
    });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    const reason = event.reason as { message?: string } | undefined;
    reportWindowError("unhandledrejection", {
      message: reason?.message ?? String(event.reason),
    });
  });
}
